import { Injectable, inject, signal } from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { BasePaginationService } from '@components/pagination/pagination.type'
import { Subject, firstValueFrom } from 'rxjs'
import { CreateWarrantyGQL, CreateWarrantyInput, CreateWarrantyMutation, GetWarrantiesTemplatesBasicGQL, GetWarrantiesTemplatesBasicQuery, PageInfo, RemoveWarrantyGQL, RemoveWarrantyInput, UpdateWarrantyGQL, UpdateWarrantyInput, WarrantiesTemplatesOrderByInput } from '../../../../../../main/src/generated/sammathnaur.graphql'
import type { Apollo } from 'apollo-angular'

@Injectable({
  providedIn: 'root',
})
export class WarrantiesService implements BasePaginationService {

  public search$: Subject<void> = new Subject()

  public readonly filterForm = new FormGroup({
    nameContains: new FormControl(''),
    type: new FormControl(''),
    maintenanceTypeId: new FormControl<string | null>(null),
  })

  public orderBy = signal<WarrantiesTemplatesOrderByInput>(WarrantiesTemplatesOrderByInput.NameAsc)
  public limit = signal<number>(25)
  public offset = signal<number>(0)
  public totalCount = signal<number>(0)
  public pageInfo = signal<PageInfo | undefined>(undefined)
  public isLoading = signal<boolean>(false)

  private readonly getWarrantiesTemplatesBasicGQL = inject(GetWarrantiesTemplatesBasicGQL)
  private readonly createWarrantyGQL = inject(CreateWarrantyGQL)
  private readonly updateWarrantyGQL = inject(UpdateWarrantyGQL)
  private readonly removeWarrantyGQL = inject(RemoveWarrantyGQL)

  public async searchPage(): Promise<Apollo.QueryResult<GetWarrantiesTemplatesBasicQuery>> {
    const { nameContains, type } = this.filterForm.value

    const observable$ = this.getWarrantiesTemplatesBasicGQL.fetch({
      variables: {
        where: {
          nameContains,
          type,
          archived: false,
          maintenanceTypeIds: this.getMaintenanceTypeIds(),
        },
        limit: this.limit(),
        offset: this.offset(),
        orderBy: this.orderBy(),
      },
    })

    return firstValueFrom(observable$)
  }

  private getMaintenanceTypeIds(): (string | null)[] | null {
    const { maintenanceTypeId, type } = this.filterForm.value

    if (type === 'equipments') {
      return null
    }

    if (maintenanceTypeId) {
      return [maintenanceTypeId, null]
    }

    return [null]
  }

  public async create(input: CreateWarrantyInput): Promise<Apollo.MutateResult<CreateWarrantyMutation>> {
    return firstValueFrom(this.createWarrantyGQL.mutate({ variables: { input } }))
  }

  public async update(input: UpdateWarrantyInput): Promise<void> {
    await firstValueFrom(this.updateWarrantyGQL.mutate({ variables: { input } }))
  }

  public async remove(input: RemoveWarrantyInput): Promise<void> {
    await firstValueFrom(this.removeWarrantyGQL.mutate({ variables: { input } }))
  }
}
